/* _customled-connector-finder.js — Custom LED owned (M8.0, 2026-10-10)
 *
 * OEM turn-signal connector finder. Same interaction model as the relay finder
 * (assets/_customled-relay-finder.js): one search box (make / model / year / OEM part number),
 * make chips, a result pill, an answer zone, and the collapsed browse tables below filtering live.
 * The matching engine is the relay finder's, ported verbatim, so both finders search identically.
 *
 * Result cards: one per matching bike row (front + rear answers cloned from that row's table cells,
 * which the server already rendered with product links, splitter lines and caveats). Nothing is
 * computed client-side about fitment — the cards only re-present the verified table.
 * Strings come from data-str-* attributes on #connector-finder (pages.connector_compat.*).
 */
(function () {
  "use strict";
  function init() {
    var finder = document.getElementById('connector-finder');
    if (!finder) return;
    var form = document.getElementById('connector-finder-form');
    var searchInput = document.getElementById('connector-search-input');
    var clearButton = document.getElementById('connector-clear');
    var pillText = document.getElementById('connector-pill-text');
    var pillWrap = document.getElementById('connector-result-pill');
    var pillJump = document.getElementById('connector-pill-jump');
    var zoneLanding = document.getElementById('connector-zone-landing');
    var zoneResults = document.getElementById('connector-zone-results');
    var zoneEmpty = document.getElementById('connector-zone-empty');
    var zoneEmptyHeading = document.getElementById('connector-zone-empty-heading');
    var browseStatus = document.getElementById('connector-browse-status');
    var rows = Array.prototype.slice.call(finder.querySelectorAll('.conn-row'));
    var groups = Array.prototype.slice.call(finder.querySelectorAll('.relay-make-group'));
    var currentMake = '';
    var MAX_CARDS = 6;
    function s(name, fallback) { return finder.getAttribute('data-str-' + name) || fallback; }
    var strings = {
      typing: s('typing', 'Keep typing…'),
      matchOne: s('match-one', '1 matching model — “{query}”'),
      matches: s('matches', '{count} matching models — “{query}”'),
      matchOneMake: s('match-one-make', '1 supported model — {make}'),
      matchesMake: s('matches-make', '{count} supported models — {make}'),
      noMatch: s('nomatch', 'No match for “{query}”'),
      emptyHeading: s('empty-heading', 'No match for “{query}”'),
      more: s('more', '+ {count} more — see the full chart below ↓'),
      front: s('front', 'Front signals'),
      rear: s('rear', 'Rear signals')
    };

    /* ---------------- matching engine (ported verbatim from the relay finder) ---------------- */
    function normalizeWords(value) {
      return String(value || '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
    }
    function normalizeCompact(value) { return normalizeWords(value).replace(/\s+/g, ''); }
    function getQueryYears(rawValue) {
      var m = String(rawValue || '').match(/\b(?:19|20)\d{2}\b/g) || [], u = [];
      m.forEach(function (y) { if (u.indexOf(y) === -1) u.push(y); });
      return u;
    }
    function getTextQuery(rawValue) { return normalizeWords(String(rawValue || '').replace(/\b(?:19|20)\d{2}\b/g, ' ')); }
    function rowMatchesYears(row, years) {
      if (!years.length) return true;
      var ry = ' ' + String(row.getAttribute('data-years') || '').replace(/\s+/g, ' ') + ' ';
      if (!ry.trim()) return false;
      return years.every(function (y) { return ry.indexOf(' ' + y + ' ') !== -1; });
    }
    function rowHasToken(row, token) {
      var t = normalizeCompact(token);
      if (!t) return true;
      if (t.length <= 3) return (' ' + row._modelWords).indexOf(' ' + t) !== -1;
      if (row._tokenMap[t]) return true;
      return row._searchCompact.indexOf(t) !== -1;
    }
    function rowMatchesText(row, q) {
      if (!q) return true;
      var qc = normalizeCompact(q);
      if (!qc) return true;
      if (qc.length > 3 && row._searchWords.indexOf(q) !== -1) return true;
      if (qc.length > 3 && row._searchCompact.indexOf(qc) !== -1) return true;
      var toks = q.split(' ').filter(Boolean);
      if (toks.length && toks.every(function (t) { return rowHasToken(row, t); })) return true;
      if (toks.length > 1 && toks.every(function (t) { return t.length > 1; })) {
        var rev = toks.slice().reverse().join('');
        if (rev.length > 3 && row._searchCompact.indexOf(rev) !== -1) return true;
      }
      return false;
    }
    rows.forEach(function (row) {
      var search = row.getAttribute('data-search');
      var modelCell = row.querySelector('.relay-model');
      row._excl = modelCell ? modelCell.querySelector('.relay-model-excl') : null;
      row._modelText = modelCell ? (row._excl ? modelCell.firstChild.textContent : modelCell.textContent).trim() : '';
      row._searchWords = normalizeWords(search);
      row._searchCompact = normalizeCompact(search);
      row._tokenMap = {};
      row._searchWords.split(' ').forEach(function (t) { if (t) row._tokenMap[t] = true; });
      row._modelWords = normalizeWords((row.getAttribute('data-make') || '') + ' ' + row._modelText);
    });

    function fmt(tpl, vars) { return tpl.replace(/\{(\w+)\}/g, function (_, k) { return vars[k] != null ? vars[k] : ''; }); }

    function computeMatches() {
      var raw = searchInput.value, q = getTextQuery(raw), years = getQueryYears(raw), make = currentMake, out = [];
      rows.forEach(function (row) {
        var ok = (!make || row.getAttribute('data-make') === make) && rowMatchesText(row, q) && rowMatchesYears(row, years);
        row.classList.toggle('relay-hidden', !ok);
        if (ok) out.push(row);
      });
      var active = !!(raw.trim() || make);
      groups.forEach(function (g) {
        var vis = g.querySelectorAll('.conn-row:not(.relay-hidden)').length;
        g.classList.toggle('relay-hidden', vis === 0);
        var desired = active ? vis > 0 : false, key = active + ':' + desired;
        if (g._openKey !== key) { g.open = desired; g._openKey = key; }
      });
      return out;
    }
    function updateBrowseStatus(count, active) {
      if (!browseStatus) return;
      var total = rows.length;
      if (!active) { browseStatus.textContent = fmt(browseStatus.getAttribute('data-str-all') || 'All {total} models, grouped by make.', { total: total }); return; }
      browseStatus.textContent = '';
      browseStatus.appendChild(document.createTextNode(fmt(browseStatus.getAttribute('data-str-filtered') || 'Showing {count} of {total} models —', { count: count, total: total }) + ' '));
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'relay-status-clear';
      b.textContent = browseStatus.getAttribute('data-str-clear') || 'clear filters to see all';
      b.addEventListener('click', function () { clearButton.click(); });
      browseStatus.appendChild(b);
    }
    function setZone(state) {
      zoneLanding.hidden = state !== 'landing';
      zoneResults.hidden = state !== 'results';
      zoneEmpty.hidden = state !== 'empty';
    }
    var announceTimer = null;
    function announce(text) { clearTimeout(announceTimer); announceTimer = setTimeout(function () { pillWrap.hidden = !text; pillText.textContent = text; }, 350); }
    function announceNow(text) { clearTimeout(announceTimer); pillWrap.hidden = !text; pillText.textContent = text; }

    function block(label, cell) {
      var wrap = document.createElement('div');
      wrap.className = 'conn-card__pos';
      var h = document.createElement('span');
      h.className = 'conn-card__poslabel';
      h.textContent = label;
      wrap.appendChild(h);
      if (cell) {
        var body = document.createElement('div');
        body.className = 'conn-card__answers';
        Array.prototype.forEach.call(cell.childNodes, function (n) { body.appendChild(n.cloneNode(true)); });
        wrap.appendChild(body);
      }
      return wrap;
    }
    function buildCards(matched) {
      zoneResults.innerHTML = '';
      var list = document.createElement('div');
      list.className = 'conn-cards';
      matched.slice(0, MAX_CARDS).forEach(function (row) {
        var card = document.createElement('article');
        card.className = 'conn-card';
        var t = document.createElement('h3');
        t.className = 'conn-card__title';
        t.setAttribute('translate', 'no');
        t.textContent = row._modelText;
        card.appendChild(t);
        if (row._excl) card.appendChild(row._excl.cloneNode(true));
        card.appendChild(block(strings.front, row.querySelector('.conn-front')));
        card.appendChild(block(strings.rear, row.querySelector('.conn-rear')));
        list.appendChild(card);
      });
      zoneResults.appendChild(list);
      if (matched.length > MAX_CARDS) {
        var more = document.createElement('a');
        more.href = '#connector-results';
        more.className = 'relay-reccard__more conn-more';
        more.textContent = fmt(strings.more, { count: matched.length - MAX_CARDS });
        zoneResults.appendChild(more);
      }
    }

    var lastKey = null, scrolled = false;
    function refresh(opts) {
      opts = opts || {};
      var raw = searchInput.value.trim(), make = currentMake, key = raw + '|' + make;
      if (key !== lastKey) { scrolled = false; lastKey = key; groups.forEach(function (g) { g._openKey = null; }); }
      try {
        var p = new URLSearchParams(window.location.search);
        if (raw) p.set('q', raw); else p.delete('q');
        if (make) p.set('make', make); else p.delete('make');
        var qs = p.toString();
        history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
      } catch (e) { /* no-op */ }
      if (!raw && !make) {
        computeMatches(); zoneResults.innerHTML = ''; setZone('landing'); pillJump.hidden = true; updateBrowseStatus(0, false); announceNow(''); return;
      }
      if (raw && raw.length < 2 && !make) {
        rows.forEach(function (r) { r.classList.remove('relay-hidden'); });
        groups.forEach(function (g) { g.classList.remove('relay-hidden'); g.open = false; g._openKey = null; });
        zoneResults.innerHTML = ''; setZone('landing'); pillJump.hidden = true; updateBrowseStatus(0, false); announce(strings.typing); return;
      }
      var matched = computeMatches();
      updateBrowseStatus(matched.length, true);
      if (!matched.length) {
        zoneResults.innerHTML = ''; setZone('empty');
        if (zoneEmptyHeading) zoneEmptyHeading.textContent = fmt(strings.emptyHeading, { query: raw || make });
        pillJump.hidden = true; announce(fmt(strings.noMatch, { query: raw || make }));
      } else {
        buildCards(matched); setZone('results'); pillJump.hidden = false;
        announce(raw ? fmt(matched.length === 1 ? strings.matchOne : strings.matches, { count: matched.length, query: raw })
                     : fmt(matched.length === 1 ? strings.matchOneMake : strings.matchesMake, { count: matched.length, make: make }));
      }
      if (opts.scroll && !scrolled) {
        var r = document.getElementById('connector-answer-zone').getBoundingClientRect();
        if (r.top > window.innerHeight * 0.6 || r.top < 0) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
        scrolled = true;
      }
    }

    var inputTimer = null;
    searchInput.addEventListener('input', function () { clearTimeout(inputTimer); inputTimer = setTimeout(function () { refresh({ scroll: false }); }, 180); });
    form.addEventListener('submit', function (e) { e.preventDefault(); clearTimeout(inputTimer); refresh({ scroll: true }); });
    finder.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-chip-make]');
      if (!chip) return;
      currentMake = chip.getAttribute('data-chip-make');
      refresh({ scroll: true });
      var zone = document.getElementById('connector-answer-zone');
      if (zone) zone.focus({ preventScroll: true });
    });
    clearButton.addEventListener('click', function () {
      searchInput.value = ''; currentMake = '';
      groups.forEach(function (g) { g.open = false; g._openKey = null; });
      refresh({ scroll: false }); searchInput.focus();
    });
    try {
      var boot = new URLSearchParams(window.location.search), q0 = boot.get('q') || '', m0 = boot.get('make') || '';
      if (q0) searchInput.value = q0;
      if (m0) currentMake = m0;
      refresh({ scroll: !!(q0 || m0) });
    } catch (e) { refresh({ scroll: false }); }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
